"use client"

import { useLayoutEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import { Tooltip } from "@/shared/ui/tooltip/tooltip"
import classes from "./overflow-tooltip.module.scss"

export type TruncateMode = "single" | "multi"

type TooltipEvents = {
  hover: boolean
  focus: boolean
  touch: boolean
}

export type OverflowTooltipProps = Readonly<{
  /** Text to display; tooltip shows the full string when truncated */
  text: string
  maxWidth?: number | string
  className?: string
  /**
   * Truncation mode.
   * - "single" (default): one-line ellipsis, measured horizontally.
   * - "multi": 2-line vertical clamp, measured vertically.
   */
  truncate?: TruncateMode
  /**
   * Override tooltip trigger events. Partial — unspecified keys keep the
   * defaults ({ hover: true, focus: true, touch: false }).
   */
  events?: Partial<TooltipEvents>
}>

/**
 * Truncates text and shows a tooltip only when overflowed.
 * Supports both single-line ellipsis and multi-line clamping via `truncate`.
 */
export function OverflowTooltip({
  text,
  maxWidth = 150,
  className,
  truncate = "single",
  events,
}: OverflowTooltipProps) {
  const containerRef = useRef<HTMLSpanElement>(null)
  const [isOverflowed, setIsOverflowed] = useState(false)
  const multiline = truncate === "multi"

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return

    const measure = () =>
      setIsOverflowed(
        multiline
          ? el.scrollHeight > el.clientHeight
          : el.scrollWidth > el.clientWidth
      )

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text, maxWidth, multiline])

  const truncatedText = (
    <span
      ref={containerRef}
      data-slot="overflow-tooltip"
      className={cn(
        multiline ? classes.multiline : classes.ellipsis,
        className
      )}
      style={{
        maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
      }}
    >
      {text}
    </span>
  )

  return (
    <Tooltip
      content={text}
      disabled={!isOverflowed}
      events={{ hover: true, focus: true, touch: false, ...events }}
    >
      {truncatedText}
    </Tooltip>
  )
}
